/**
 * V1.5 Mission Control — KL-Persona-Default.
 * Quelle: Implementation Brief §7.2 + Screen-Inventory §3.
 *
 * Block-Hierarchie:
 *   1. Greeter (Eyebrow + H-Display + Mono-Stack rechts)
 *   2. AI Briefing Strip (Mock — echtes /v1/ai/dashboard-briefing in Phase 3.2)
 *   3. 3 Attention-Cards (red/amber/primary, computed aus DB-Flags)
 *   4. Dual-Grid: What's stuck (links) + Across phases (rechts)
 *   5. Today's movement (Activity-Feed)
 *
 * Persona-Adaption (BL/PL): Phase 9.
 */
import { useQuery } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Arbeitsliste, useArbeitslisteAnzahl } from '../../components/v2/befunde/arbeitsliste.js';
import { InfoKnopf } from '../../components/v2/hilfe/info-knopf.js';
import { SectionHead } from '../../components/v2/section-head.js';
import { api } from '../../lib/api.js';
import { useFeatureFlag } from '../../lib/features.js';
import { personaPraedikat, personaSegmente } from '../../lib/persona-scope.js';
import { personaLabel, usePersona } from '../../lib/persona.js';
import { useSession } from '../../lib/session.js';

/** Composite-Project-Shape, wie /v1/projects es nach V1.5-Refactor liefert. */
type Project = {
  project_id: string;
  project_key: string;
  name: string;
  customer_name: string;
  segment: string;
  phase_id: string;
  phase_changed_at: string;
  lifecycle_status: import('@projektverwaltung/shared').LifecycleStatus;
  staffing_needed: boolean;
  is_blocked?: boolean;
  blocked_reason?: string | null;
  qs_with_bl_open?: boolean;
  project_lead_user_id?: string | null;
  manager_user_id?: string | null;
  responsibility?: string | null;
  /** #140: Manager-Scope über eigene Tags (`tags[].manager_user_id`). */
  tags?: Array<{ manager_user_id?: string | null }>;
};

const DAY_MS = 86_400_000;

export function MissionControlPage() {
  const { t } = useTranslation();
  const { user } = useSession();
  const persona = usePersona();
  const showBriefing = useFeatureFlag('ai_dashboard_briefing');
  const befundeAn = useFeatureFlag('befunde');
  // Mit Flag nennt die Begrüßung die Arbeitsliste (Stapel + Verantwortungsbereich), nicht die Kennzahlen.
  const befundAnzahl = useArbeitslisteAnzahl(befundeAn);
  const [reiter, setReiter] = useState<McReiter>('arbeitsliste');
  const projectsQuery = useQuery({
    queryKey: ['v2-mc-projects'],
    queryFn: () => api.listProjects({}) as Promise<{ items: Project[] }>,
  });
  const phasesQuery = useQuery({
    queryKey: ['v2-mc-phases'],
    queryFn: () => api.listPhases(),
  });
  const praedikat = useMemo(
    () =>
      personaPraedikat(persona.active, user?.user_id, personaSegmente(user?.function_roles ?? [])),
    [persona.active, user],
  );
  // Eingegrenzte Personas filtern „Heute Bewegung" im Browser — der Server kennt keinen Scope.
  // Mit dem globalen Limit 20 verschwänden die eigenen Ereignisse an einem regen Tag hinter
  // fremden; deshalb dann das Server-Maximum (100) holen und erst danach kürzen.
  const aktivitaetsLimit = praedikat ? 100 : 20;
  const activityQuery = useQuery({
    queryKey: ['v2-mc-activity', aktivitaetsLimit],
    queryFn: () => api.listRecentActivity({ since_hours: 12, limit: aktivitaetsLimit }),
    refetchInterval: 60_000,
  });

  const items = projectsQuery.data?.items ?? [];
  const allActive = useMemo(() => items.filter((p) => p.lifecycle_status === 'active'), [items]);

  // Mission Control = "meine Welt": harter Persona-Scope (nicht abschaltbar).
  // #140: dieselbe Definition wie der Persona-Filter im Phase Board (`personaPraedikat`):
  // PL → eigene Projekte, Manager → eigener Tag oder eigenes Manager-Feld im eigenen Segment,
  // BL → eigenes Segment, MTB/Service → eigener Bereich im eigenen Segment. KL/Admin: global.
  const active = useMemo(
    () => (praedikat ? allActive.filter(praedikat) : allActive),
    [allActive, praedikat],
  );
  // „Heute Bewegung" im selben Scope (Review #117/#140). `items` sind die geladenen Projekte
  // (`listProjects` liefert aktive); der Activity-Endpunkt liefert ebenfalls nur aktive.
  const imScope = useMemo(
    () => (praedikat ? new Set(items.filter(praedikat).map((p) => p.project_id)) : null),
    [items, praedikat],
  );
  const bewegungen = (activityQuery.data?.items ?? []).filter(
    (r) => !imScope || imScope.has(r.project_id),
  );

  const blocked = active.filter((p) => p.is_blocked);
  const withStaffingNeed = active.filter((p) => p.staffing_needed);
  const withQsBl = active.filter((p) => p.qs_with_bl_open);

  const phaseDistribution = useMemo(
    () => buildPhaseDistribution(active, phasesQuery.data?.items ?? []),
    [active, phasesQuery.data],
  );
  const stuck = useMemo(() => buildStuck(active), [active]);

  // Heutiger Inhalt — mit Flag `befunde` im Reiter „Kennzahlen", ohne Flag unverändert (A9).
  const kennzahlen = (
    <>
      <AttentionRow blocked={blocked} staffing={withStaffingNeed} qsbl={withQsBl} />

      <div className="grid grid-cols-1 lg:grid-cols-[1.3fr_1fr] gap-6">
        <StuckCard items={stuck} />
        <PhaseDistributionCard rows={phaseDistribution} totalActive={active.length} />
      </div>

      <MovementStrip
        recentItems={buildMovementFromActivity(bewegungen, t as never)}
        sinceHours={activityQuery.data?.since_hours ?? 12}
      />

      <PersonaSection persona={persona.active} myUserId={user?.user_id} active={active} />
    </>
  );

  return (
    <div className="max-w-[var(--canvas-max)] mx-auto px-2 pb-12 space-y-10">
      <Greeter
        userName={user?.name ?? 'Gast'}
        attentionCount={
          befundeAn
            ? (befundAnzahl ?? 0)
            : blocked.length + withStaffingNeed.length + withQsBl.length
        }
        activeCount={active.length}
      />

      {befundeAn ? (
        <>
          <ReiterLeiste aktiv={reiter} onWechsel={setReiter} />
          {/* Das Platzhalter-KI-Briefing entfällt mit der Arbeitsliste (A9). */}
          {reiter === 'arbeitsliste' ? <Arbeitsliste /> : kennzahlen}
        </>
      ) : (
        <>
          {showBriefing && (
            <BriefingStrip
              blocked={blocked.length}
              staffing={withStaffingNeed.length}
              qsbl={withQsBl.length}
            />
          )}
          {kennzahlen}
        </>
      )}
    </div>
  );
}

// ===========================================================================
// Block 6 — Persona-spezifische Sektion (Phase 9)
// ===========================================================================
// kl / mtb / service  → keine extra Sektion (KL ist Default-Sicht)
// bl / manager        → "QS-mit-BL Arbeitsvorrat"
// pl                  → "Meine Projekte" (project_lead_user_id = me)

function PersonaSection({
  persona,
  myUserId,
  active,
}: {
  persona: ReturnType<typeof usePersona>['active'];
  myUserId: string | undefined;
  active: Project[];
}) {
  const { t } = useTranslation();
  // #140: `active` ist schon nach `personaPraedikat` eingegrenzt — hier keine zweite,
  // abweichende Definition von „mein Bestand" (früher Manager nur über das Manager-Feld).
  if (persona === 'bl' || persona === 'manager') {
    const qsItems = active.filter((p) => p.qs_with_bl_open);
    return (
      <section className="animate-fade-up-d3">
        <SectionHead
          label={t('v2.mc.section_qs_bl', 'QS-mit-BL Arbeitsvorrat')}
          meta={t('v2.mc.qs_bl_meta', '{{persona}} · {{count}} offen', {
            persona: personaLabel(persona),
            count: qsItems.length,
          })}
          emphasized={t('v2.mc.qs_bl_emphasis', 'Arbeitsvorrat')}
        />
        <Card>
          {qsItems.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
              {t('v2.mc.qs_bl_empty', 'Aktuell kein QS-mit-BL-Vorrat in deinem Scope.')}
            </p>
          ) : (
            <ul className="divide-y" style={{ borderColor: 'var(--line-soft)' }}>
              {qsItems.map((p) => (
                <li key={p.project_id} className="py-2 first:pt-0 last:pb-0">
                  <Link
                    to={`/projects/${p.project_id}`}
                    className="flex items-center gap-3 text-sm hover:translate-x-0.5 transition-transform"
                    style={{ textDecoration: 'none', color: 'var(--ink)' }}
                  >
                    <span
                      style={{ fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--ink-3)' }}
                    >
                      {p.project_key}
                    </span>
                    <span className="flex-1 truncate">{p.customer_name}</span>
                    <Tag tone="primary">qs-bl</Tag>
                    {p.is_blocked && <Tag tone="danger">block</Tag>}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>
    );
  }

  if (persona === 'pl' && myUserId) {
    const myProjects = active; // schon auf project_lead_user_id = ich eingegrenzt (#140)
    return (
      <section className="animate-fade-up-d3">
        <SectionHead
          label={t('v2.mc.section_my_projects', 'Meine Projekte')}
          meta={t('v2.mc.section_my_projects_meta', 'Projektleitung · {{count}}', {
            count: myProjects.length,
          })}
          emphasized={t('v2.mc.my_projects_emphasis', 'Meine')}
        />
        <Card>
          {myProjects.length === 0 ? (
            <p className="text-sm" style={{ color: 'var(--ink-3)' }}>
              {t('v2.mc.my_projects_empty', 'Du leitest aktuell keine Projekte.')}
            </p>
          ) : (
            <ul className="divide-y" style={{ borderColor: 'var(--line-soft)' }}>
              {myProjects.map((p) => (
                <li key={p.project_id} className="py-2 first:pt-0 last:pb-0">
                  <Link
                    to={`/projects/${p.project_id}`}
                    className="flex items-center gap-3 text-sm hover:translate-x-0.5 transition-transform"
                    style={{ textDecoration: 'none', color: 'var(--ink)' }}
                  >
                    <span
                      style={{ fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--ink-3)' }}
                    >
                      {p.project_key}
                    </span>
                    <span className="flex-1 truncate">{p.customer_name}</span>
                    {p.is_blocked && <Tag tone="danger">block</Tag>}
                    {p.qs_with_bl_open && <Tag tone="primary">qs-bl</Tag>}
                    {p.staffing_needed && <Tag tone="warn">staffing</Tag>}
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </section>
    );
  }

  return null;
}

// ===========================================================================
// Block 1 — Greeter
// ===========================================================================

function Greeter({
  userName,
  attentionCount,
  activeCount,
}: { userName: string; attentionCount: number; activeCount: number }) {
  const { t, i18n } = useTranslation();
  const now = new Date();
  const dateLabel = new Intl.DateTimeFormat(i18n.language, {
    weekday: 'short',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(now);
  const timeLabel = new Intl.DateTimeFormat(i18n.language, {
    hour: '2-digit',
    minute: '2-digit',
  }).format(now);
  const greeting = computeGreeting(now.getHours(), t as never);
  const firstName = userName.split(' ')[0] ?? userName;

  let emphasis: string;
  let trail: string;
  if (attentionCount === 0) {
    emphasis = t('v2.mc.att_zero_lead', 'Alles');
    trail = t('v2.mc.att_zero_trail', 'läuft glatt heute.');
  } else if (attentionCount === 1) {
    emphasis = t('v2.mc.att_one_lead', 'Eine Sache');
    trail = t('v2.mc.att_one_trail', 'braucht dich heute.');
  } else {
    emphasis = t('v2.mc.att_many_lead', '{{n}} Sachen', { n: germanNumber(attentionCount) });
    trail = t('v2.mc.att_many_trail', 'brauchen dich heute.');
  }

  return (
    <section className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-6 pt-6 animate-fade-up">
      <div>
        <div
          className="text-[10.5px] uppercase tracking-[0.14em] mb-3 flex items-center gap-2"
          style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
        >
          <span>
            {t('v2.mc.eyebrow', 'Mission Control')} · {dateLabel} · {timeLabel}
          </span>
          <InfoKnopf themaId="datenstand" />
        </div>
        <h1
          className="font-medium leading-[1.05] tracking-tight"
          style={{
            fontFamily: 'var(--f-display)',
            color: 'var(--ink)',
            fontSize: 'clamp(26px, 3vw, 42px)',
          }}
        >
          {greeting}, {firstName}.
          <br />
          <em
            className="not-italic"
            style={{ color: 'var(--primary)', fontStyle: 'italic', fontWeight: 400 }}
          >
            {emphasis}
          </em>{' '}
          {trail}
        </h1>
      </div>
      <div className="flex flex-col items-end justify-center text-right gap-1.5">
        <div
          style={{
            fontFamily: 'var(--f-display)',
            fontSize: 42,
            color: 'var(--ink)',
            lineHeight: 1,
          }}
        >
          {timeLabel}
        </div>
        <div className="text-xs" style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}>
          {t('v2.mc.sync_healthy', 'Salesforce sync · live · healthy')}
        </div>
        <div className="text-xs" style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}>
          {t('v2.mc.active_projects', '{{count}} aktive Projekte', { count: activeCount })}
        </div>
      </div>
    </section>
  );
}

// ===========================================================================
// Block 2 — AI Briefing Strip (Mock)
// ===========================================================================

function BriefingStrip({
  blocked,
  staffing,
  qsbl,
}: { blocked: number; staffing: number; qsbl: number }) {
  const { t } = useTranslation();
  const blockedText =
    blocked === 0
      ? t('v2.mc.briefing_blocked_none', 'kein Projekt blockiert')
      : blocked === 1
        ? t('v2.mc.briefing_blocked_one', '{{count}} Projekt blockiert', { count: blocked })
        : t('v2.mc.briefing_blocked_many', '{{count}} Projekte blockiert', { count: blocked });
  const staffingSuffix =
    staffing > 0
      ? t('v2.mc.briefing_staffing_suffix', ', {{count}} mit offener Personalbedarfs-Lage', {
          count: staffing,
        })
      : '';
  const qsblSuffix =
    qsbl > 0
      ? t('v2.mc.briefing_qsbl_suffix', ', {{count}} im QS-mit-BL-Arbeitsvorrat', { count: qsbl })
      : '';
  return (
    <section className="animate-fade-up-d1">
      <Card glow>
        <div className="flex items-center gap-3 mb-3">
          <Tag tone="primary">
            {t('v2.mc.briefing_title', 'AI Briefing · gpt-orchestrate-2.1 · mock')}
          </Tag>
          <span
            className="text-[10.5px]"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
          >
            {t('v2.mc.briefing_generated', 'generated · just now')}
          </span>
          <div className="ml-auto flex gap-2">
            <button
              type="button"
              className="text-xs px-2.5 py-1 rounded-md transition-colors"
              style={{ color: 'var(--ink-2)', background: 'var(--surface-2)' }}
            >
              {t('v2.mc.regenerate', 'Regenerate')}
            </button>
            <button
              type="button"
              className="text-xs px-2.5 py-1 rounded-md transition-colors"
              style={{ color: 'var(--ink-2)', background: 'var(--surface-2)' }}
            >
              {t('v2.mc.save_to_library', 'Save to library')}
            </button>
          </div>
        </div>
        <p
          className="leading-relaxed"
          style={{
            fontFamily: 'var(--f-display)',
            fontStyle: 'italic',
            fontSize: 22,
            color: 'var(--ink)',
          }}
        >
          {t('v2.mc.briefing_today_prefix', 'Heute ist ')}
          <span style={{ color: 'var(--primary)', fontStyle: 'normal', fontWeight: 500 }}>
            {blockedText}
          </span>
          {staffingSuffix}
          {qsblSuffix}.{' '}
          <em style={{ color: 'var(--ink-2)' }}>
            {t(
              'v2.mc.briefing_recommendation',
              'Empfehlung: zuerst die roten Karten oben, dann die Staffing-Lücken.',
            )}
          </em>
        </p>
      </Card>
    </section>
  );
}

// ===========================================================================
// Block 3 — Attention Cards
// ===========================================================================

function AttentionRow({
  blocked,
  staffing,
  qsbl,
}: { blocked: Project[]; staffing: Project[]; qsbl: Project[] }) {
  const { t } = useTranslation();
  const cards = [
    blocked[0] && {
      tone: 'danger' as const,
      eyebrow: t('v2.mc.att_eyebrow_block', 'Block-Flag'),
      number: '01',
      project: blocked[0],
      title: blocked[0].customer_name,
      body:
        blocked[0].blocked_reason ??
        t('v2.mc.att_body_block_default', 'Projekt ist blockiert — Begründung nachpflegen.'),
    },
    staffing[0] && {
      tone: 'warn' as const,
      eyebrow: t('v2.mc.att_eyebrow_staffing', 'Staffing offen'),
      number: '02',
      project: staffing[0],
      title: staffing[0].customer_name,
      body: t('v2.mc.att_body_staffing', '{{name}} — Personalbedarf flagged. Kandidaten prüfen.', {
        name: staffing[0].name,
      }),
    },
    qsbl[0] && {
      tone: 'primary' as const,
      eyebrow: t('v2.mc.att_eyebrow_qsbl', 'QS mit BL'),
      number: '03',
      project: qsbl[0],
      title: qsbl[0].customer_name,
      body: t(
        'v2.mc.att_body_qsbl',
        '{{name}} — QS-mit-BL-Flag offen. Mit Bereichsleitung abstimmen.',
        { name: qsbl[0].name },
      ),
    },
  ].filter(Boolean) as NonNullable<{
    tone: 'danger' | 'warn' | 'primary';
    eyebrow: string;
    number: string;
    project: Project;
    title: string;
    body: string;
  }>[];

  if (cards.length === 0) {
    return (
      <section className="animate-fade-up-d2">
        <SectionHead
          label={t('v2.mc.section_attention', 'Was braucht dich heute')}
          meta={t('v2.mc.att_meta_zero', '0 items · alles bewegt sich')}
        />
        <Card>
          <div className="py-8 text-center text-sm" style={{ color: 'var(--ink-3)' }}>
            {t(
              'v2.mc.att_all_smooth',
              'Alles bewegt sich. Keine roten Flags, kein Staffing-Druck, kein QS-Vorrat.',
            )}
          </div>
        </Card>
      </section>
    );
  }

  return (
    <section className="animate-fade-up-d2">
      <SectionHead
        label={t('v2.mc.section_attention', 'Was braucht dich heute')}
        meta={t('v2.mc.att_meta_some', '{{count}} items · ranked by impact', {
          count: cards.length,
        })}
      />
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {cards.map((c, i) => (
          <AttentionCard key={`${c.tone}-${c.project.project_id}`} {...c} delay={i} />
        ))}
      </div>
    </section>
  );
}

function AttentionCard({
  tone,
  eyebrow,
  number,
  project,
  title,
  body,
}: {
  tone: 'danger' | 'warn' | 'primary';
  eyebrow: string;
  number: string;
  project: Project;
  title: string;
  body: string;
  delay?: number;
}) {
  const accentVar =
    tone === 'danger' ? 'var(--danger)' : tone === 'warn' ? 'var(--warn)' : 'var(--primary)';
  return (
    <Link
      to={`/projects/${project.project_id}`}
      className="block group transition-transform hover:-translate-y-0.5"
      style={{ textDecoration: 'none' }}
    >
      <Card>
        <div
          className="absolute left-0 top-3 bottom-3 w-[3px] rounded-r-full"
          style={{ background: accentVar, boxShadow: `0 0 10px ${accentVar}` }}
        />
        <div className="flex items-baseline gap-2 mb-2">
          <span
            className="text-[10.5px] uppercase tracking-[0.14em]"
            style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
          >
            {eyebrow}
          </span>
          <span
            className="ml-auto"
            style={{
              fontFamily: 'var(--f-display)',
              fontSize: 28,
              color: 'var(--ink-3)',
              lineHeight: 1,
            }}
          >
            {number}
          </span>
        </div>
        <div className="text-[15px] font-medium mb-1" style={{ color: 'var(--ink)' }}>
          {title}
        </div>
        <div className="text-xs leading-relaxed" style={{ color: 'var(--ink-3)' }}>
          {body}
        </div>
      </Card>
    </Link>
  );
}

// ===========================================================================
// Block 4 — Dual-Grid (Stuck + Phase Distribution)
// ===========================================================================

function StuckCard({ items }: { items: Array<{ project: Project; days: number }> }) {
  const { t } = useTranslation();
  return (
    <section className="animate-fade-up-d3">
      <SectionHead
        label={t('v2.mc.section_stuck', 'Was steckt fest')}
        meta={t('v2.mc.stuck_meta', 'keine Bewegung > 5 Tage')}
        emphasized={t('v2.mc.stuck_emphasis', 'steckt fest')}
      />
      <Card padding={false}>
        {items.length === 0 ? (
          <div className="py-10 text-center text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.mc.stuck_empty', 'Alles bewegt sich.')}
          </div>
        ) : (
          <ul className="divide-y" style={{ borderColor: 'var(--line-soft)' }}>
            {items.slice(0, 6).map(({ project, days }) => (
              <li key={project.project_id} style={{ borderColor: 'var(--line-soft)' }}>
                <Link
                  to={`/projects/${project.project_id}`}
                  className="grid grid-cols-[8px_88px_1fr_140px_60px_24px] items-center gap-3 px-4 py-3 transition-colors hover:bg-[var(--surface-2)]"
                  style={{ textDecoration: 'none' }}
                >
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{ background: project.is_blocked ? 'var(--danger)' : 'var(--warn)' }}
                  />
                  <span
                    style={{ fontFamily: 'var(--f-mono)', fontSize: 12, color: 'var(--ink-2)' }}
                  >
                    {project.project_key}
                  </span>
                  <div>
                    <div className="text-sm" style={{ color: 'var(--ink)' }}>
                      {project.customer_name}
                    </div>
                    <div className="text-[11px]" style={{ color: 'var(--ink-3)' }}>
                      {project.segment}
                    </div>
                  </div>
                  <span
                    style={{ fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--ink-3)' }}
                  >
                    {project.name}
                  </span>
                  <span style={{ fontFamily: 'var(--f-mono)', fontSize: 11, color: 'var(--warn)' }}>
                    {days}d
                  </span>
                  <span style={{ color: 'var(--ink-3)' }}>›</span>
                </Link>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </section>
  );
}

function PhaseDistributionCard({
  rows,
  totalActive,
}: { rows: Array<{ name: string; count: number }>; totalActive: number }) {
  const { t } = useTranslation();
  const max = rows.reduce((m, r) => Math.max(m, r.count), 1);
  return (
    <section className="animate-fade-up-d4">
      <SectionHead
        label={t('v2.mc.section_phase_dist', 'Phasenverteilung')}
        meta={t('v2.mc.phase_dist_meta', 'aktive Projekte deiner Sicht')}
        emphasized={t('v2.mc.phase_dist_emphasis', 'Phasen')}
      />
      <Card>
        <div className="space-y-3">
          {rows.map((r) => (
            <div key={r.name} className="grid grid-cols-[100px_1fr_30px] items-center gap-3">
              <span
                style={{
                  fontFamily: 'var(--f-mono)',
                  fontSize: 11,
                  color: 'var(--ink-3)',
                  textTransform: 'uppercase',
                  letterSpacing: '0.08em',
                }}
              >
                {r.name}
              </span>
              <div
                className="h-2 rounded-full overflow-hidden"
                style={{ background: 'var(--surface-2)' }}
              >
                <div
                  className="h-full transition-all duration-700"
                  style={{
                    width: `${(r.count / max) * 100}%`,
                    background: 'var(--primary)',
                    boxShadow: `0 0 8px var(--primary-soft)`,
                  }}
                />
              </div>
              <span
                style={{
                  fontFamily: 'var(--f-mono)',
                  fontSize: 11,
                  color: 'var(--ink-2)',
                  textAlign: 'right',
                }}
              >
                {r.count}
              </span>
            </div>
          ))}
        </div>
        <div
          className="mt-4 pt-4 border-t flex items-center text-xs"
          style={{ borderColor: 'var(--line-soft)', color: 'var(--ink-3)' }}
        >
          <span>{t('v2.mc.phase_changes_today', 'Phasenwechsel heute')}</span>
          <span className="ml-auto" style={{ fontFamily: 'var(--f-mono)' }}>
            <span style={{ color: 'var(--primary)' }}>
              {t('v2.mc.phase_changes_forward', '{{count}} vorwärts', { count: 0 })}
            </span>
            {' · '}
            <span style={{ color: 'var(--warn)' }}>
              {t('v2.mc.phase_changes_rejected', '{{count}} abgelehnt', { count: 0 })}
            </span>
          </span>
        </div>
        <div
          className="mt-1 text-[10px]"
          style={{ color: 'var(--ink-3)', fontFamily: 'var(--f-mono)' }}
        >
          {t('v2.mc.phase_dist_total', 'Gesamt: {{count}} aktiv', { count: totalActive })}
        </div>
      </Card>
    </section>
  );
}

// ===========================================================================
// Block 5 — Movement Strip
// ===========================================================================

type MovementItem = {
  tone: 'primary' | 'warn' | 'danger' | 'success';
  label: string;
  text: string;
  when: string;
  projectId?: string;
};

function MovementStrip({
  recentItems,
  sinceHours,
}: { recentItems: MovementItem[]; sinceHours: number }) {
  const { t } = useTranslation();
  return (
    <section className="animate-fade-up-d5">
      <SectionHead
        label={t('v2.mc.section_movement', 'Heute Bewegung')}
        meta={t('v2.mc.movement_meta', 'letzte {{h}} Stunden', { h: sinceHours })}
        emphasized={t('v2.mc.movement_emphasis', 'Heute')}
      />
      <Card padding={false}>
        {recentItems.length === 0 ? (
          <div className="py-8 text-center text-sm" style={{ color: 'var(--ink-3)' }}>
            {t('v2.mc.movement_empty', 'Heute noch keine Bewegung.')}
          </div>
        ) : (
          <ul className="divide-y" style={{ borderColor: 'var(--line-soft)' }}>
            {recentItems.map((it, i) => {
              const row = (
                <>
                  <Tag tone={it.tone}>{it.label}</Tag>
                  <span className="text-sm" style={{ color: 'var(--ink-2)' }}>
                    {it.text}
                  </span>
                  <span
                    className="ml-auto text-[11px]"
                    style={{ fontFamily: 'var(--f-mono)', color: 'var(--ink-3)' }}
                  >
                    {it.when}
                  </span>
                </>
              );
              return it.projectId ? (
                <li key={i} style={{ borderColor: 'var(--line-soft)' }}>
                  <Link
                    to={`/projects/${it.projectId}`}
                    className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--surface-2)] transition-colors"
                  >
                    {row}
                  </Link>
                </li>
              ) : (
                <li
                  key={i}
                  className="flex items-center gap-3 px-4 py-3"
                  style={{ borderColor: 'var(--line-soft)' }}
                >
                  {row}
                </li>
              );
            })}
          </ul>
        )}
      </Card>
    </section>
  );
}

// ===========================================================================
// Primitives
// ===========================================================================

function Card({
  children,
  glow = false,
  padding = true,
}: { children: React.ReactNode; glow?: boolean; padding?: boolean }) {
  return (
    <div
      className="relative rounded-xl border"
      style={{
        background: 'var(--surface)',
        borderColor: 'var(--line-soft)',
        boxShadow: glow
          ? 'var(--shadow-card-hover), 0 0 24px var(--primary-soft)'
          : 'var(--shadow-card)',
        padding: padding ? '20px' : 0,
      }}
    >
      {children}
    </div>
  );
}

function Tag({
  children,
  tone,
}: { children: React.ReactNode; tone: 'primary' | 'warn' | 'danger' | 'success' }) {
  const bg =
    tone === 'primary'
      ? 'var(--primary-soft)'
      : tone === 'warn'
        ? 'var(--warn-soft)'
        : tone === 'danger'
          ? 'var(--danger-soft)'
          : 'var(--success-soft)';
  const fg =
    tone === 'primary'
      ? 'var(--primary)'
      : tone === 'warn'
        ? 'var(--warn)'
        : tone === 'danger'
          ? 'var(--danger)'
          : 'var(--success)';
  return (
    <span
      className="inline-flex items-center px-2 py-0.5 rounded text-[10px] uppercase tracking-[0.1em] font-medium"
      style={{ background: bg, color: fg, fontFamily: 'var(--f-mono)' }}
    >
      {children}
    </span>
  );
}

type McReiter = 'arbeitsliste' | 'kennzahlen';

/** Reiter „Arbeitsliste" / „Kennzahlen" — nur mit Flag `befunde` (PV-052, A9). */
function ReiterLeiste({ aktiv, onWechsel }: { aktiv: McReiter; onWechsel: (r: McReiter) => void }) {
  const { t } = useTranslation();
  const reiter: Array<{ key: McReiter; label: string }> = [
    { key: 'arbeitsliste', label: t('v2.mc.tab_arbeitsliste', 'Arbeitsliste') },
    { key: 'kennzahlen', label: t('v2.mc.tab_kennzahlen', 'Kennzahlen') },
  ];
  return (
    <div className="flex items-end gap-3 border-b" style={{ borderColor: 'var(--line-soft)' }}>
      <div role="tablist" aria-label={t('v2.mc.tabs_label', 'Ansicht')} className="flex gap-1">
        {reiter.map((r) => (
          <button
            key={r.key}
            type="button"
            role="tab"
            aria-selected={aktiv === r.key}
            onClick={() => onWechsel(r.key)}
            className="px-3 py-2 text-sm -mb-px border-b-2"
            style={{
              borderColor: aktiv === r.key ? 'var(--primary)' : 'transparent',
              color: aktiv === r.key ? 'var(--ink)' : 'var(--ink-3)',
              fontWeight: aktiv === r.key ? 600 : 400,
            }}
          >
            {r.label}
          </button>
        ))}
      </div>
      {/* PV-037: Erklärung der Befunde direkt an der Arbeitsliste (nur bei Flag `befunde`). */}
      <span className="ml-auto self-center pb-1">
        <InfoKnopf themaId="befunde" />
      </span>
    </div>
  );
}

// ===========================================================================
// Helpers
// ===========================================================================

function computeGreeting(hour: number, t: (key: string, fallback?: string) => string): string {
  if (hour < 11) return t('v2.mc.greeting_morning', 'Guten Morgen');
  if (hour < 14) return t('v2.mc.greeting_midday', 'Mittag');
  if (hour < 18) return t('v2.mc.greeting_afternoon', 'Guten Nachmittag');
  return t('v2.mc.greeting_evening', 'Guten Abend');
}

function germanNumber(n: number): string {
  const map: Record<number, string> = {
    2: 'Zwei',
    3: 'Drei',
    4: 'Vier',
    5: 'Fünf',
    6: 'Sechs',
    7: 'Sieben',
    8: 'Acht',
    9: 'Neun',
  };
  return map[n] ?? String(n);
}

function buildPhaseDistribution(
  active: Project[],
  phases: Array<{ phase_id: string; name: string; order: number }>,
): Array<{ name: string; count: number }> {
  const byPhase = new Map<string, number>();
  for (const p of active) byPhase.set(p.phase_id, (byPhase.get(p.phase_id) ?? 0) + 1);
  return phases
    .slice()
    .sort((a, b) => a.order - b.order)
    .map((ph) => ({ name: ph.name, count: byPhase.get(ph.phase_id) ?? 0 }));
}

function buildStuck(active: Project[]): Array<{ project: Project; days: number }> {
  const now = Date.now();
  return active
    .map((p) => ({
      project: p,
      days: Math.floor((now - new Date(p.phase_changed_at).getTime()) / DAY_MS),
    }))
    .filter((x) => x.days >= 5)
    .sort((a, b) => b.days - a.days);
}

type ActivityRow = {
  history_id: string;
  project_id: string;
  project_key: string;
  customer_name: string;
  event_type: string;
  note: string | null;
  changed_at: string;
  changed_by_name: string | null;
};

function buildMovementFromActivity(
  rows: ActivityRow[],
  t: (key: string, fallback?: string, opts?: Record<string, unknown>) => string,
): MovementItem[] {
  return rows.slice(0, 10).map((r) => {
    const meta = mapEventTypeToDisplay(r.event_type, t);
    const text = t('v2.mc.movement_text_event', '{{customer}}{{note}}', {
      customer: r.customer_name,
      note: r.note ? ` · ${r.note}` : '',
    });
    return {
      tone: meta.tone,
      label: meta.label,
      text,
      when: formatRelativeTime(r.changed_at, t),
      projectId: r.project_id,
    };
  });
}

function mapEventTypeToDisplay(
  eventType: string,
  t: (key: string, fallback?: string, opts?: Record<string, unknown>) => string,
): { tone: MovementItem['tone']; label: string } {
  switch (eventType) {
    case 'phase_change':
      return { tone: 'primary', label: t('v2.mc.movement_event_phase_change', 'Phase') };
    case 'lifecycle_completed':
      return { tone: 'success', label: t('v2.mc.movement_event_completed', 'Abschluss') };
    case 'lifecycle_reactivated':
      return { tone: 'primary', label: t('v2.mc.movement_event_reactivated', 'Reakt.') };
    case 'qs_with_bl_flagged':
      return { tone: 'primary', label: t('v2.mc.movement_event_qsbl_open', 'QS-BL') };
    case 'qs_with_bl_resolved':
      return { tone: 'success', label: t('v2.mc.movement_event_qsbl_done', 'QS-BL✓') };
    case 'blocked_flagged':
      return { tone: 'danger', label: t('v2.mc.movement_event_block_set', 'Block') };
    case 'blocked_resolved':
      return { tone: 'success', label: t('v2.mc.movement_event_block_done', 'Block✓') };
    case 'staffing_request_created':
      return { tone: 'warn', label: t('v2.mc.movement_event_staffing_new', 'Staffing') };
    case 'staffing_request_resolved':
      return { tone: 'success', label: t('v2.mc.movement_event_staffing_done', 'Staffing✓') };
    case 'responsibility_assigned':
      return { tone: 'primary', label: t('v2.mc.movement_event_assign', 'Zuweisung') };
    case 'handover_field_completed':
      return { tone: 'primary', label: t('v2.mc.movement_event_handover_field', 'Übergabe') };
    case 'auftrag_merged_into_project':
      return { tone: 'primary', label: t('v2.mc.movement_event_merge', 'Merge') };
    case 'auftrag_split_from_project':
      return { tone: 'primary', label: t('v2.mc.movement_event_split', 'Split') };
    case 'sync_refresh':
      return { tone: 'primary', label: t('v2.mc.movement_event_sync', 'Sync') };
    case 'mention_mail_sent':
      return { tone: 'primary', label: t('v2.mc.movement_event_mention', 'Mention') };
    // #131
    case 'auftrag_sf_geloescht':
      return { tone: 'danger', label: t('v2.mc.movement_event_sf_geloescht', 'SF gelöscht') };
    case 'auftrag_sf_wiederhergestellt':
      return { tone: 'warn', label: t('v2.mc.movement_event_sf_wieder', 'SF wieder da') };
    case 'sf_uebertragung_verworfen':
      return { tone: 'warn', label: t('v2.mc.movement_event_sf_verworfen', 'SF verworfen') };
    case 'project_fields_updated':
      // Erscheint hier normalerweise nicht — der Feed filtert Feldaenderungen aus
      // (routes/activity.ts). Der Fall bleibt abgedeckt, falls sich das aendert.
      return { tone: 'primary', label: t('v2.mc.movement_event_fields_updated', 'Feld') };
    default:
      return { tone: 'primary', label: eventType };
  }
}

function formatRelativeTime(
  iso: string,
  t: (key: string, fallback?: string, opts?: Record<string, unknown>) => string,
): string {
  const diffMin = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 60_000));
  if (diffMin < 1) return t('v2.mc.movement_now', 'jetzt');
  if (diffMin < 60) return t('v2.mc.movement_min_ago', 'vor {{n}}min', { n: diffMin });
  const diffHour = Math.floor(diffMin / 60);
  if (diffHour < 24) return t('v2.mc.movement_hour_ago', 'vor {{n}}h', { n: diffHour });
  const diffDay = Math.floor(diffHour / 24);
  return t('v2.mc.movement_day_ago', 'vor {{n}}d', { n: diffDay });
}
